export type RetryStrategy = {
    retries?: number;
    backoff?: number;
    retryOnStatus?: number[];
    maxBackoff?: number;
    timeout?: number; // 新增超时参数
};

let _inMemoryAccessToken: string | null = null;
export function setInMemoryAccessToken(token: string | null): void {
    _inMemoryAccessToken = token;
}

export function isIOSBrowser(): boolean {
    // FIXME(iOS-auth): 这是临时兜底。终极方案应让前端与 API 同站/同域（反代），回到纯 HttpOnly Cookie（SameSite=Lax）并移除 localStorage token。
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const iOSDevice = /iPad|iPhone|iPod/i.test(ua);
    // iPadOS 13+ 可能伪装成 Mac，但具备触控点
    const iPadOS13Plus = /Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
    const iOSBrowserTokens = /(CriOS|FxiOS|EdgiOS)/i.test(ua);
    return iOSDevice || iPadOS13Plus || iOSBrowserTokens;
}

export async function ReliableFetch(
    input: RequestInfo,
    init: RequestInit = {},
    retryStrategy: RetryStrategy = {}
): Promise<Response> {

    // iOS/Safari 可能会拦截跨站 Cookie（ITP/第三方 Cookie 限制），仅在 iOS 下启用 Header token 兜底。
    // FIXME(iOS-auth): 等前端与 API 同站/同域后移除此逻辑，避免 token 暴露给 JS。
    // 如果已显式设置 Authorization，则尊重调用方。
    if (isIOSBrowser()) {
        try {
            const token = _inMemoryAccessToken || localStorage.getItem("access_token");
            if (token) {
                const headers = new Headers(init.headers || {});
                if (!headers.has("Authorization")) {
                    headers.set("Authorization", `Bearer ${token}`);
                    init = { ...init, headers };
                }
            }
        } catch {
            // 忽略 localStorage 不可用等异常
        }
    }

    // 非幂等不能随便重连，暂时还是用原始的吧
    return await fetch(input, init);


    const {
        retries = 3,
        backoff = 500,
        retryOnStatus = [408, 429, 500, 502, 503, 504],
        maxBackoff = 5000,
        timeout = 8000, // 默认超时 8 秒
    } = retryStrategy;

    let wait = backoff;

    for (let attempt = 0; attempt <= retries; attempt++) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(input, {
                ...init,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            // ✅ 2xx 响应，直接返回
            if (response.ok) return response;

            // ❌ 非重试状态码，抛出错误
            if (!retryOnStatus.includes(response.status)) {
                throw new Error(`HTTP ${response.status} - 不可重试状态码`);
            }

            // ❗可重试的状态码
            throw new Error(`HTTP ${response.status} - 可重试错误`);

        } catch (err) {
            clearTimeout(timeoutId);

            // 最后一次重试后抛出错误
            if (attempt === retries) {
                const error = err instanceof Error ? err : new Error("未知网络错误");
                error.message = `最终请求失败（尝试 ${retries + 1} 次）: ${error.message}`;
                throw error;
            }

            // 网络错误或可重试状态码，等待后重试
            console.warn(`[ReliableFetch] 第 ${attempt + 1} 次尝试失败，等待 ${wait}ms 后重试...`, err);
            await new Promise((resolve) => setTimeout(resolve, wait));
            wait = Math.min(wait * 2, maxBackoff);
        }
    }

    throw new Error("逻辑不应执行到此处");
}
